import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  Box,
} from "@mui/material";
import { useState } from "react";
import { updateReceiptItemPrices } from "../../api/adminStockApi";
import { styled } from "@mui/material/styles";

export type VariantItem = {
  id: number;
  variant_id: number;
  sku: string;
  variant_name: string;
  product_name: string;
  unit_cost: string;
  price: string; // retail price
  quantity: number;
  total_cost: string;
  variant_image_url?: string;
  product_image_url?: string;
};

type Props = {
  receiptId: number;
  items: VariantItem[];
  onClose: () => void;
  onUpdated: () => void;
};

const PriceInput = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    textAlign: "right",
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
}));

const EditPricesModal: React.FC<Props> = ({ receiptId, items, onClose, onUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [editedItems, setEditedItems] = useState(
    items.map((item) => ({
      ...item,
      unit_cost: parseFloat(item.unit_cost).toFixed(2),
      price: parseFloat(item.price).toFixed(2),
    }))
  );

  const handleChange = (id: number, field: "unit_cost" | "price", value: string) => {
    setEditedItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSave = async () => {
    const payload = editedItems.map((item) => ({
      id: item.id,
      variant_id: item.variant_id,
      unit_cost: parseFloat(item.unit_cost),
      price: parseFloat(item.price),
    }));

    try {
      await updateReceiptItemPrices(receiptId, payload);
      onUpdated();
      onClose();
    } catch (err) {
      console.error("Failed to update prices", err);
      alert("Failed to save. Check console.");
    }
  };

  const renderDesktopView = () => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <Grid container spacing={1} sx={{ fontWeight: "bold", py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Grid item xs={2}>Product</Grid>
          <Grid item xs={1}>SKU</Grid>
          <Grid item xs={1} sx={{ textAlign: "right" }}>Qty</Grid>
          <Grid item xs={2} sx={{ textAlign: "right" }}>Unit Cost</Grid>
          <Grid item xs={2} sx={{ textAlign: "right" }}>Retail Price</Grid>
          <Grid item xs={1} sx={{ textAlign: "right" }}>Markup</Grid>
          <Grid item xs={1} sx={{ textAlign: "right" }}>Margin</Grid>
          <Grid item xs={2} sx={{ textAlign: "right" }}>Total Cost</Grid>
        </Grid>
      </Grid>
      {editedItems.map((item) => {
        const unitCost = parseFloat(item.unit_cost);
        const retailPrice = parseFloat(item.price);
        const markup = unitCost > 0 ? ((retailPrice - unitCost) / unitCost) * 100 : 0;
        const margin = retailPrice > 0 ? ((retailPrice - unitCost) / retailPrice) * 100 : 0;
        const total = (unitCost * item.quantity).toFixed(2);
        const imgSrc = item.variant_image_url || item.product_image_url || "/placeholder.png";

        return (
          <Grid item xs={12} key={item.id}>
            <Grid container spacing={1} alignItems="center" sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
              <Grid item xs={2}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar 
                    src={imgSrc} 
                    alt={item.variant_name}
                    sx={{ width: 40, height: 40, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{item.product_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.variant_name}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={1}>
                <Typography variant="body2">{item.sku}</Typography>
              </Grid>
              <Grid item xs={1} sx={{ textAlign: "right" }}>
                <Typography variant="body2">{item.quantity}</Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "right" }}>
                <PriceInput
                  size="small"
                  value={item.unit_cost}
                  onChange={(e) => handleChange(item.id, "unit_cost", e.target.value)}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ maxWidth: 120 }}
                />
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "right" }}>
                <PriceInput
                  size="small"
                  value={item.price}
                  onChange={(e) => handleChange(item.id, "price", e.target.value)}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ maxWidth: 120 }}
                />
              </Grid>
              <Grid item xs={1} sx={{ textAlign: "right" }}>
                <Typography variant="body2" color={markup > 0 ? "success.main" : "error.main"}>
                  {markup.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={1} sx={{ textAlign: "right" }}>
                <Typography variant="body2" color={margin > 0 ? "success.main" : "error.main"}>
                  {margin.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "right" }}>
                <Typography variant="body2" fontWeight="medium">
                  ${total}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderMobileView = () => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {editedItems.map((item) => {
        const unitCost = parseFloat(item.unit_cost);
        const retailPrice = parseFloat(item.price);
        const markup = unitCost > 0 ? ((retailPrice - unitCost) / unitCost) * 100 : 0;
        const margin = retailPrice > 0 ? ((retailPrice - unitCost) / retailPrice) * 100 : 0;
        const total = (unitCost * item.quantity).toFixed(2);
        const imgSrc = item.variant_image_url || item.product_image_url || "/placeholder.png";

        return (
          <Grid item xs={12} key={item.id}>
            <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[1] }}>
              <CardContent>
                <Box sx={{ display: "flex", mb: 2 }}>
                  <Avatar 
                    src={imgSrc} 
                    alt={item.variant_name}
                    sx={{ width: 56, height: 56, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">{item.product_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.variant_name}</Typography>
                    <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quantity</Typography>
                    <Typography variant="body1">{item.quantity}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                    <Typography variant="body1" fontWeight="medium">${total}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                    <PriceInput
                      fullWidth
                      size="small"
                      value={item.unit_cost}
                      onChange={(e) => handleChange(item.id, "unit_cost", e.target.value)}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Retail Price</Typography>
                    <PriceInput
                      fullWidth
                      size="small"
                      value={item.price}
                      onChange={(e) => handleChange(item.id, "price", e.target.value)}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Markup</Typography>
                    <Typography variant="body1" color={markup > 0 ? "success.main" : "error.main"}>
                      {markup.toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Margin</Typography>
                    <Typography variant="body1" color={margin > 0 ? "success.main" : "error.main"}>
                      {margin.toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Dialog 
      open 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          [theme.breakpoints.down("sm")]: {
            margin: 1,
            width: "calc(100% - 16px)",
          },
        },
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: theme.palette.primary.main, 
        color: "primary.contrastText",
        fontWeight: "bold",
        py: 2,
      }}>
        Edit Prices
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? 1 : 3 }}>
        {isMobile ? renderMobileView() : renderDesktopView()}
      </DialogContent>
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: "1px solid #eee",
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPricesModal;